import { Cmd } from "@typescript-tea/core";
import { exhaustiveCheck } from "ts-exhaustive-check";
import * as LanguageText from "@genesys/shared/lib/language-texts";
import { SystemStatus } from "@genesys/shared/lib/enums/system-status";
import { CaseType } from "@genesys/shared/lib/enums/case-type";
import {
  CtorsUnion,
  ctorsUnion
} from "@genesys/client-core/lib/constructors-union";
import * as SharedState from "../../shared-state";
import * as GraphQlTypes from "../../graphql-types";
import { promiseCmd } from "../../promise-effect-manager";
import * as Navigation from "../../navigation-effect-manager";
import { clientConfig, Environment } from "../../config";
import { SystemEvent } from "../../system-events";
import * as LoadedState from "../state";
import * as Product from "../product";
import * as System from "../system";
import * as PropertyFilterHelpers from "@genesys/shared/lib/property-filter-helpers";
import { handleConfiguratorActions } from "./configurator-action-handlers";
import { updateSystemMutation } from "./update-system-mutation";
import { PropertyFilter } from "@genesys/property";
import * as Authorization from "@genesys/shared/lib/authorization";
import * as Types from "./types";
import { ConfiguratorAction } from "./configurator-actions";
import * as Queries from "./queries";

export type State =
  | Types.SystemLoadedState
  | Types.SystemLoadingState
  | Types.SystemExpiredState
  | Types.SystemError;

interface ComponentIdParam {
  readonly type: "componentId";
  readonly componentId: string;
}

interface AccessoriesParam {
  readonly type: "accessories";
  readonly componentId: string;
}

interface ModalParam {
  readonly type: "modal";
  readonly modal: string;
}

interface TraceIdParam {
  readonly type: "traceId";
  readonly traceId: string;
}

type Params = ComponentIdParam | ModalParam | AccessoriesParam | TraceIdParam;
type ModalTypes = Types.SystemLoadedState["loadedState"]["modal"]["type"];

export const init = (
  sharedState: SharedState.State,
  genesysNoParam: string,
  otherParams?: Params
): readonly [State, Cmd<Action>?, SharedState.Action?] => {
  const genesysNoFormat = /^[dsg]{1}[0-9]+?-[0-9]+?$/i;

  if (!genesysNoFormat.test(genesysNoParam)) {
    return [
      {
        mode: "error",
        errorMessage: "Invalid GenesysNoFromat",
        traceId: undefined
      }
    ];
  }

  const split = genesysNoParam.split("-");
  const genesysNo = parseInt(split[0].substring(1, 7), 10);
  const genesysPrefix = split[0][0];

  if (
    genesysPrefix.toLowerCase() !== getGenesysPrefix(clientConfig.environment)
  ) {
    return [
      {
        mode: "error",
        errorMessage: `Invalid Genesys prefix "${genesysPrefix}" for enviroment ${clientConfig.environment}`,
        traceId: undefined
      },
      undefined,
      SharedState.Action.loadLastOpenedSystemsAndFavorites()
    ];
  }
  const revisionNo = parseInt(split[1], 10);
  return [
    {
      mode: "loading"
    },
    loadData(genesysNo, revisionNo, sharedState, otherParams),
    SharedState.Action.loadLastOpenedSystemsAndFavorites()
  ];
};

function loadData(
  genesysNo: number,
  revisionNo: number,
  sharedState: SharedState.State,
  params?: Params
): Cmd<Action> {
  return promiseCmd<
    Action,
    | {
        readonly type: "ok";
        readonly user: GraphQlTypes.SystemConfiguratorUser;
        readonly product: GraphQlTypes.SystemConfiguratorProduct;
      }
    | {
        readonly type: "error";
        readonly message?: string;
      }
    | {
        readonly type: "expired";
        readonly systemId: string;
        readonly genesysNo: number;
        readonly revisionNo: number;
      }
    | {
        readonly type: "unauthorized";
      }
  >(
    async () => {
      let validation;
      try {
        validation = await sharedState.graphQL.queryUser<
          GraphQlTypes.SystemConfiguratorValidationUser,
          GraphQlTypes.SystemConfiguratorValidationUserVariables
        >(Queries.systemConfiguratorValidationQuery, {
          genesysNo: genesysNo,
          revisionNo: revisionNo
        });
      } catch (err) {
        return {
          type: "error",
          message: err
        };
      }
      if (!validation.user.validateSystemGenesysNo) {
        return {
          type: "error",
          message: "System file not found"
        };
      }

      if (!validation.user.validateSystemGenesysNo.hasPermission) {
        return {
          type: "unauthorized"
        };
      }

      if (!validation.user.validateSystemGenesysNo.isValid) {
        return {
          type: "expired",
          systemId: validation.user.validateSystemGenesysNo.systemId,
          genesysNo: genesysNo,
          revisionNo: revisionNo
        };
      }

      const user = await sharedState.graphQL.queryUser<
        GraphQlTypes.SystemConfiguratorUser,
        GraphQlTypes.SystemConfiguratorUserVariables
      >(Queries.systemConfiguratorQuery, {
        genesysNo: genesysNo,
        revisionNo: revisionNo
      });

      if (!user.user.systemByGenesysNo) {
        return {
          type: "error"
        };
      }

      const product = await sharedState.graphQL.queryProduct<
        GraphQlTypes.SystemConfiguratorProduct,
        GraphQlTypes.SystemConfiguratorProductVariables
      >(Queries.systemConfiguratorProductQuery, {
        systemType: {
          systemTypeId: user.user.systemByGenesysNo.systemFile.systemTypeId
        },
        systemProduct: {
          productId: user.user.systemByGenesysNo.systemFile.systemTypeId + "SYS"
        }
      });

      return {
        type: "ok",
        user: user,
        product: product
      };
    },
    result => {
      switch (result.type) {
        case "ok":
          return Action.loadingComplete({
            system: System.parseSystem(result.user.user.systemByGenesysNo!),
            productData: Product.parseProductData(
              result.product.product,
              sharedState.translate
            ),
            params
          });
        case "error":
          return Action.systemError(new Error(result.message || "error"));
        case "expired":
          return Action.systemExpiredDialog(
            result.systemId,
            result.genesysNo,
            result.revisionNo
          );
        case "unauthorized":
          return Action.systemError(new Error("unauthorized"));
        default:
          throw new Error("Unknown type");
      }
    },
    rej => Action.systemError(rej)
  );
}
function getGenesysPrefix(environment: Environment): Types.GenesysPrefix {
  switch (environment) {
    case "Develop":
    case "Localhost": {
      return "d";
    }
    case "Production": {
      return "g";
    }
    case "Staging": {
      return "s";
    }

    default:
      return exhaustiveCheck(environment, true);
  }
}

function openExpiredSystem(
  systemId: string,
  genesysNo: number,
  revisionNo: number,
  sharedState: SharedState.State
): Cmd<Action> {
  return promiseCmd<
    Action,
    | {
        readonly type: "ok";
        readonly user: GraphQlTypes.SystemConfiguratorUser;
        readonly product: GraphQlTypes.SystemConfiguratorProduct;
      }
    | {
        readonly type: "error";
        readonly message?: string;
      }
    | {
        readonly type: "expired";
        readonly systemId: string;
        readonly genesysNo: number;
        readonly revisionNo: number;
      }
    | {
        readonly type: "unauthorized";
      }
  >(
    async () => {
      const res = await sharedState.graphQL.queryUser<
        GraphQlTypes.UpdateSystemStatus,
        GraphQlTypes.UpdateSystemStatusVariables
      >(updateSystemMutation, {
        input: {
          forceUpdate: true,
          systemId: systemId,
          targetStatus: SystemStatus.ConfigureSystemSuccess
        }
      });

      if (res.updateSystem.__typename === "UpdateSystemRejectResultType") {
        return {
          type: "error",
          message: res.updateSystem.rejectReason
        };
      }

      let validation;
      try {
        validation = await sharedState.graphQL.queryUser<
          GraphQlTypes.SystemConfiguratorValidationUser,
          GraphQlTypes.SystemConfiguratorValidationUserVariables
        >(Queries.systemConfiguratorValidationQuery, {
          genesysNo: genesysNo,
          revisionNo: revisionNo
        });
      } catch (err) {
        return {
          type: "error",
          message: err
        };
      }

      if (!validation.user.validateSystemGenesysNo) {
        return {
          type: "error",
          message: "System file not found"
        };
      }

      if (!validation.user.validateSystemGenesysNo.hasPermission) {
        return {
          type: "unauthorized"
        };
      }

      if (!validation.user.validateSystemGenesysNo.isValid) {
        return {
          type: "expired",
          systemId: validation.user.validateSystemGenesysNo.systemId,
          genesysNo: genesysNo,
          revisionNo: revisionNo
        };
      }

      const user = await sharedState.graphQL.queryUser<
        GraphQlTypes.SystemConfiguratorUser,
        GraphQlTypes.SystemConfiguratorUserVariables
      >(Queries.systemConfiguratorQuery, {
        genesysNo: genesysNo,
        revisionNo: revisionNo
      });

      if (!user.user.systemByGenesysNo) {
        return {
          type: "error"
        };
      }

      const product = await sharedState.graphQL.queryProduct<
        GraphQlTypes.SystemConfiguratorProduct,
        GraphQlTypes.SystemConfiguratorProductVariables
      >(Queries.systemConfiguratorProductQuery, {
        systemType: {
          systemTypeId: user.user.systemByGenesysNo.systemFile.systemTypeId
        },
        systemProduct: {
          productId: user.user.systemByGenesysNo.systemFile.systemTypeId + "SYS"
        }
      });

      return {
        type: "ok",
        user: user,
        product: product
      };
    },
    result => {
      switch (result.type) {
        case "ok":
          return Action.loadingComplete({
            system: System.parseSystem(result.user.user.systemByGenesysNo!),
            productData: Product.parseProductData(
              result.product.product,
              sharedState.translate
            )
          });
        case "error":
          return Action.systemError(new Error(result.message ?? "error"));
        case "expired":
          return Action.systemExpiredDialog(
            result.systemId,
            result.genesysNo,
            result.revisionNo
          );
        case "unauthorized":
          return Action.systemError(new Error("unauthorized"));
        default:
          throw new Error("Unknown type");
      }
    },
    rej => Action.systemError(rej)
  );
}

export const Action = ctorsUnion({
  GotSystemEvent: (event: SystemEvent) => ({ event }),
  loadingComplete: (data: {
    readonly system: System.System;
    readonly productData: Product.ProductData;
    readonly params?: Params;
  }) => ({
    data
  }),
  loadingSystemComplete: (data: System.System | undefined) => ({
    data
  }),
  systemMutationComplete: (
    errorMessage: string | undefined,
    traceId: string | undefined
  ) => ({
    errorMessage,
    traceId
  }),
  updateSystemNonBlockingStarted: (traceId: string | undefined) => ({
    traceId
  }),
  systemError: (error: Error) => ({ error }),
  systemExpiredDialog: (
    systemId: string,
    genesysNo: number,
    revisionNo: number
  ) => ({ systemId, genesysNo, revisionNo }),
  lowerStatusAndLoad: () => ({}),
  dispatchLoadedSystem: (action: LoadedState.Action) => ({ action }),
  redirect: (url: string) => ({ url }),
  calculateEnergy: (system: System.System) => ({ system })
});
export type Action = CtorsUnion<typeof Action>;

export function update(
  action: Action,
  state: State,
  sharedState: SharedState.State
): readonly [
  State,
  Cmd<Action>?,
  ReadonlyArray<SharedState.Action | undefined>?
] {
  switch (action.type) {
    case "loadingComplete": {
      // Helper Functions
      const getFullGenesysNumber = () =>
        sharedState.genesysPrefix.genesysNo(
          action.data.system.file.genesysNo,
          action.data.system.revisionNo
        );

      const updateBrowserTitle = () => {
        const fullGno = getFullGenesysNumber();
        const systemFileName = action.data.system.file.name;
        return SharedState.Action.setBrowserTitle(
          `${fullGno} ${systemFileName}`
        );
      };

      const convertToPascalCase = (input: string) =>
        input
          .toLowerCase()
          .split("-")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join("");

      const isScreenTypeSelectable = (
        screenType: Product.Screen["screenType"]
      ) => {
        const sysComponent = action.data.system.components.find(c =>
          c.productId.endsWith("SYS")
        )!;
        const { applicationClaims: userClaims } = sharedState.user;

        const screen = action.data.productData.systemType.screens.find(
          x => x.screenType === screenType
        );
        if (!screen) {
          console.error(`Screen not found: ${screenType}`);
          return false;
        }

        return (
          PropertyFilter.isValid(
            sysComponent.properties,
            screen.propertyFilter
          ) &&
          Authorization.checkClaimFilter(
            userClaims,
            PropertyFilter.fromString(screen.claimFilter || "") ||
              PropertyFilter.Empty
          )
        );
      };

      const canOpenModal = (modalType: ModalTypes): boolean => {
        const modalTypeDef = getInitModalTypeDef(modalType);

        if (!modalTypeDef) {
          return false;
        }

        if (modalTypeDef.screenType) {
          if (!isScreenTypeSelectable(modalTypeDef.screenType)) {
            return false;
          }
          return modalTypeDef.isVisible
            ? modalTypeDef.isVisible({
                system: action.data.system,
                productData: action.data.productData,
                userClaims: sharedState.user.applicationClaims
              })
            : true;
        }

        return true;
      };

      // Modal Initialization Logic
      const initializeModal = (
        params: Params
      ): Types.LoadedTypes.OnInitModal | undefined => {
        const type = params?.type;
        if (!type) {
          return undefined;
        }
        switch (type) {
          case "modal": {
            const modalType = convertToPascalCase(params!.modal);
            const modalTypeDef = getInitModalTypeDef(modalType);

            if (!modalTypeDef) {
              console.error(`Unknown modal type: ${modalType}`);
              return undefined;
            }

            if (!canOpenModal(modalType as ModalTypes)) {
              console.error(
                `User does not have permission to open modal: ${modalType}`
              );
              return undefined;
            }

            return {
              type: modalType as Types.LoadedTypes.OnOnitOtherModals["type"]
            };
          }

          case "componentId": {
            const componentId = params?.componentId;
            const component = action.data.system.components.find(
              c => c.id === componentId
            );

            if (!component) {
              console.error(`Component with id ${componentId} not found`);
              return undefined;
            }

            return { type: "ComponentEditor", componentId };
          }

          case "accessories": {
            const componentId = params.componentId;
            const component = action.data.system.components.find(
              c => c.id === componentId
            );

            if (!component) {
              console.error(`Component with id ${componentId} not found`);
              return undefined;
            }

            return { type: "Accessories", componentId };
          }
          case "traceId": {
            return undefined;
          }
          default:
            exhaustiveCheck(type, true);
        }
      };

      // Execution Logic
      const browserTitleAction = updateBrowserTitle();

      let initModal: Types.LoadedTypes.OnInitModal | undefined = undefined;
      if (action.data.params) {
        initModal = initializeModal(action.data.params);
      }

      const [loadedState, sharedStateAction, loadedStateCmd] = LoadedState.init(
        sharedState,
        action.data.system,
        action.data.productData,
        initModal
      );

      const traceId =
        action.data.params?.type === "traceId"
          ? action.data.params.traceId
          : undefined;

      return [
        {
          ...state,
          mode: "loaded",
          loadedState,
          traceId: traceId
        },
        Cmd.map(Action.dispatchLoadedSystem, loadedStateCmd),
        [sharedStateAction, browserTitleAction]
      ];
    }

    case "systemError":
      return [
        {
          ...state,
          mode: "error",
          traceId: undefined,
          errorMessage: action.error.message
        }
      ];
    case "dispatchLoadedSystem":
      if (state.mode === "loaded") {
        const [
          updatedState,
          configuratorActions,
          sharedStateActions,
          loadedCmd,
          shouldReloadSystem
        ] = LoadedState.update(action.action, state.loadedState, sharedState);
        const withLoadedState: State = { ...state, loadedState: updatedState };
        const [newState, cmds] = handleConfiguratorActions(
          configuratorActions,
          withLoadedState,
          sharedState
        );
        return [
          shouldReloadSystem &&
          newState.mode === "loaded" &&
          newState.loadedState.overlay.type === "OverlayNone"
            ? {
                ...newState,
                loadedState: {
                  ...newState.loadedState,
                  overlay: {
                    type: "OverlayQuery",
                    text: sharedState.translate(
                      LanguageText.calculationProgress("fetching")
                    )
                  }
                }
              }
            : newState,
          Cmd.batch<Action>([
            ...(cmds || []),
            Cmd.map(Action.dispatchLoadedSystem, loadedCmd),
            ...(shouldReloadSystem
              ? [
                  sharedState.graphQL.queryUserCmd<
                    GraphQlTypes.SystemConfiguratorUser,
                    GraphQlTypes.SystemConfiguratorUserVariables,
                    Action
                  >(
                    Queries.systemConfiguratorQuery,
                    {
                      genesysNo: state.loadedState.system.file.genesysNo,
                      revisionNo: state.loadedState.system.revisionNo
                    },
                    result =>
                      Action.loadingSystemComplete(
                        result.user.systemByGenesysNo
                          ? System.parseSystem(result.user.systemByGenesysNo)
                          : undefined
                      )
                  )
                ]
              : [])
          ]),
          sharedStateActions
        ];
      }
      return [state];

    case "calculateEnergy": {
      if (state.mode === "loaded") {
        const binCaseIds = action.system.operatingCases
          .filter(oc => oc.caseType === CaseType.Bin)
          .map(oc => oc.id);

        const [newState, cmds] = handleConfiguratorActions(
          [ConfiguratorAction.calculateEnergy(binCaseIds)],
          state,
          sharedState
        );

        return [newState, Cmd.batch<Action>([...(cmds || [])]), undefined];
      }
      return [state];
    }

    case "systemMutationComplete": {
      if (action.errorMessage) {
        return [
          {
            mode: "error",
            traceId: action.traceId,
            errorMessage: action.errorMessage
          }
        ];
      }

      if (state.mode !== "loaded") {
        return [state];
      }

      if (state.loadedState.runningMutations > 1) {
        return [
          {
            ...state,
            traceId: action.traceId,
            loadedState: {
              ...state.loadedState,
              runningMutations: state.loadedState.runningMutations - 1
            }
          }
        ];
      }

      return [
        {
          ...state,
          traceId: action.traceId,
          loadedState: {
            ...state.loadedState,
            runningMutations: 0
          }
        },
        sharedState.graphQL.queryUserCmd<
          GraphQlTypes.SystemConfiguratorUser,
          GraphQlTypes.SystemConfiguratorUserVariables,
          Action
        >(
          Queries.systemConfiguratorQuery,
          {
            genesysNo: state.loadedState.system.file.genesysNo,
            revisionNo: state.loadedState.system.revisionNo
          },
          result =>
            Action.loadingSystemComplete(
              result.user.systemByGenesysNo
                ? System.parseSystem(result.user.systemByGenesysNo)
                : undefined
            )
        )
      ];
    }
    case "loadingSystemComplete": {
      if (state.mode !== "loaded") {
        return [state];
      }

      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            system: action.data!,
            overlay: { type: "OverlayNone" }
          }
        }
      ];
    }
    case "redirect":
      return [
        {
          ...state
        },
        Navigation.pushUrl(action.url)
      ];
    case "systemExpiredDialog":
      return [
        {
          ...state,
          mode: "expired",
          systemId: action.systemId,
          genesysNo: action.genesysNo,
          revisionNo: action.revisionNo
        }
      ];
    case "lowerStatusAndLoad":
      if (state.mode !== "expired") {
        return [state];
      }
      return [
        {
          ...state,
          mode: "loading"
        },

        openExpiredSystem(
          state.systemId,
          state.genesysNo,
          state.revisionNo,
          sharedState
        )
      ];
    case "GotSystemEvent": {
      if (state.mode !== "loaded") {
        return [state];
      }
      const [newState, cmd] = getStateFromSystemEvent(
        sharedState,
        state,
        action.event
      );
      return [newState, cmd];
    }
    case "updateSystemNonBlockingStarted": {
      if (state.mode !== "loaded") {
        return [state];
      }
      return [{ ...state, traceId: action.traceId }];
    }
    default:
      return exhaustiveCheck(action, true);
  }
}
function getStateFromSystemEvent(
  sharedState: SharedState.State,
  state: Extract<State, { readonly mode: "loaded" }>,
  systemEvent: SystemEvent
): [State, Cmd<Action> | undefined] {
  const targetStatuses =
    state.loadedState.overlay.type !== "OverlayQuery" &&
    state.loadedState.overlay.type !== "OverlayNone"
      ? state.loadedState.overlay.targetStatuses
      : [];
  switch (systemEvent.type) {
    case "SystemMutationStarted": {
      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            overlay: { type: "OverlayDefault", targetStatuses }
          }
        },
        undefined
      ];
    }
    case "StepStarted": {
      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            overlay: {
              type: "OverlayWithText",
              targetStatuses,
              text: sharedState.translate(
                LanguageText.calculationProgress(systemEvent.name)
              )
            }
          }
        },
        undefined
      ];
    }
    case "SystemPersisting": {
      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            overlay: {
              type: "OverlayWithText",
              targetStatuses,
              text: sharedState.translate(
                LanguageText.calculationProgress("saving")
              )
            }
          }
        },
        undefined
      ];
    }
    case "SystemPersisted": {
      if (!systemEvent.wasSuccess) {
        return reloadSystem(state, sharedState);
      }

      const overlayState = state.loadedState.overlay;
      if (
        overlayState.type === "OverlayNone" ||
        overlayState.type === "OverlayQuery"
      ) {
        return [state, undefined];
      }

      const targetStatusesLeft = overlayState.targetStatuses.slice(1);

      if (targetStatusesLeft.length > 0) {
        return [
          {
            ...state,
            loadedState: {
              ...state.loadedState,
              overlay: { ...overlayState, targetStatuses: targetStatusesLeft }
            }
          },
          undefined
        ];
      }

      return reloadSystem(state, sharedState);
    }
    case "SystemMutationException": {
      return [
        {
          mode: "error",
          errorMessage: systemEvent.rejectReason,
          traceId: state.traceId
        },
        undefined
      ];
    }
    case "StepCompleted":
    case "EnergyBinCalculationsCompleted": {
      return [state, undefined];
    }
    case "EnergyBinCalculationStarted": {
      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            overlay: {
              type: "OverlayBinCalculations",
              casesLeft: systemEvent.cases,
              total: systemEvent.cases.length,
              targetStatuses
            }
          }
        },
        undefined
      ];
    }
    case "EnergyBinCalculationCompleted": {
      if (state.loadedState.overlay.type !== "OverlayBinCalculations") {
        return [state, undefined];
      }
      return [
        {
          ...state,
          loadedState: {
            ...state.loadedState,
            overlay: {
              type: "OverlayBinCalculations",
              casesLeft: state.loadedState.overlay.casesLeft.filter(
                c => c !== systemEvent.caseId
              ),
              total: state.loadedState.overlay.total,
              targetStatuses
            }
          }
        },
        undefined
      ];
    }
    default: {
      exhaustiveCheck(systemEvent, true);
    }
  }
}

type InitModalTypeDef = {
  readonly screenType: Product.Screen["screenType"] | undefined;
  readonly isVisible?: (params: {
    readonly system: System.System;
    readonly productData: Product.ProductData;
    readonly userClaims: SharedState.User["applicationClaims"];
  }) => boolean;
};
function getInitModalTypeDef(name: string): InitModalTypeDef | undefined {
  // Helper object to ensure that all modal types are accounted for
  const modals: Record<
    Exclude<ModalTypes, "NoModal" | "Accessories" | "ComponentEditor">,
    InitModalTypeDef
  > = {
    MechanicalSketch: {
      screenType: GraphQlTypes.ScreenType.MECHANICAL_SKETCH
    },
    SystemAccessories: {
      screenType: GraphQlTypes.ScreenType.SYSTEM_ACCESSORIES
    },
    BuildInRevit: {
      screenType: GraphQlTypes.ScreenType.REVIT_BUILDER
    },
    CondensationAnalysis: {
      screenType: GraphQlTypes.ScreenType.CONDENSATION_ANALYSIS
    },
    Energy: {
      screenType: GraphQlTypes.ScreenType.ENERGY
    },
    MultipleSystemsEditor: {
      screenType: GraphQlTypes.ScreenType.MULTIPLE_SYSTEMS_EDITOR
    },
    DataCenterEditor: {
      screenType: GraphQlTypes.ScreenType.DATA_CENTER_EDITOR
    },
    Print: {
      screenType: GraphQlTypes.ScreenType.SYSTEM_PRINTOUT
    },
    Pid: {
      screenType: GraphQlTypes.ScreenType.PID_DIAGRAM
    },
    BuildInSc: {
      screenType: GraphQlTypes.ScreenType.SOLID_CONTACT_BUILDER
    },
    WiringDiagram: {
      screenType: GraphQlTypes.ScreenType.WIRING_DIAGRAM
    },
    LockedSystemCalculation: {
      screenType: undefined,
      isVisible: ({ system, userClaims }) => {
        const systemStatus = system.status;
        const isDev = Authorization.checkPermission(
          userClaims,
          Authorization.genesysUserClaims.developer
        );
        const isSystemLocked = systemStatus >= SystemStatus.LockSuccess;
        return isDev && isSystemLocked;
      }
    },
    MCompare: {
      screenType: GraphQlTypes.ScreenType.M_COMPARE
    },
    NotesAndDocuments: {
      screenType: undefined
    },
    OperatingCases: {
      screenType: GraphQlTypes.ScreenType.OPERATING_CASES
    },
    PlenumSize: {
      screenType: GraphQlTypes.ScreenType.SYSTEM_SIZE
    },
    Price: {
      screenType: GraphQlTypes.ScreenType.ADJUST_PRICE,
      isVisible: ({ system, userClaims, productData }) => {
        const adjustPriceScreen = productData.systemType.screens.find(
          x => x.screenType === GraphQlTypes.ScreenType.ADJUST_PRICE
        );

        const claimFilterString = adjustPriceScreen?.claimFilter
          ? adjustPriceScreen.claimFilter.replace(
              /\{systemsalesorg\}/,
              system.salesOrganisationId
            )
          : "";

        return (
          !!adjustPriceScreen &&
          Authorization.checkClaimFilter(
            userClaims,
            PropertyFilterHelpers.createPropertyFilter(
              claimFilterString,
              PropertyFilter.fromString("1=0")!
            )
          )
        );
      }
    },
    PropertyGroup: {
      screenType: GraphQlTypes.ScreenType.PROPERTY_GROUP
    },
    PsychrometricChart: {
      screenType: GraphQlTypes.ScreenType.PSYCHROMETRICS_CHART
    }
  };

  return modals[name as keyof typeof modals];
}

function reloadSystem(
  state: Types.SystemLoadedState,
  sharedState: SharedState.State
): [State, Cmd<Action>] {
  return [
    {
      ...state,
      loadedState: {
        ...state.loadedState,
        overlay: {
          type: "OverlayQuery",
          text: sharedState.translate(
            LanguageText.calculationProgress("fetching")
          )
        }
      }
    },
    sharedState.graphQL.queryUserCmd<
      GraphQlTypes.SystemConfiguratorUser,
      GraphQlTypes.SystemConfiguratorUserVariables,
      Action
    >(
      Queries.systemConfiguratorQuery,
      {
        genesysNo: state.loadedState.system.file.genesysNo,
        revisionNo: state.loadedState.system.revisionNo
      },
      result =>
        Action.loadingSystemComplete(
          result.user.systemByGenesysNo
            ? System.parseSystem(result.user.systemByGenesysNo)
            : undefined
        )
    )
  ];
}
//tslint:disable-next-line
